import React, { useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { FaCheckCircle, FaCar, FaChartLine, FaClipboardList, FaMapMarkerAlt, FaBell, FaCog, FaRegFileAlt } from 'react-icons/fa';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useStore from '../store/useStore';

const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => {
  return (
    <div
      {...tooltipProps}
      className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-sm w-full mx-4 sm:mx-0 animate-fade-in-up"
    >
      <div className="flex items-center mb-3">
        {step.icon && (
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-sm mr-3">
            <step.icon className="text-lg" />
          </div>
        )}
        <h2 className="text-xl font-bold text-gray-900">
          {step.title}
        </h2>
      </div>
      
      <div className="text-gray-600 text-sm leading-relaxed mb-6">
        {step.content}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-50">
        <button
          {...skipProps}
          className="text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
        >
          Skip Tour
        </button>
        <div className="flex space-x-2">
          {index > 0 && (
            <button
              {...backProps}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
          )}
          <button
            {...primaryProps}
            className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 transform"
          >
            {isLastStep ? 'Okay' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OnboardingTour = () => {
  const [run, setRun] = useState(false);
  const vendorId = useStore((state) => state.vendorId);

  useEffect(() => {
    const checkTourState = async () => {
      // 1. Fast local check
      if (localStorage.getItem('americar_tour_status') === 'completed') {
        return;
      }

      if (!vendorId) return;
      try {
        const vendorRef = doc(db, 'vendorUsers', vendorId);
        const vendorSnap = await getDoc(vendorRef);
        if (vendorSnap.exists()) {
          const data = vendorSnap.data();
          // Show tour if status is pending or doesn't exist yet
          if (data.tourStatus !== 'completed') {
            setTimeout(() => setRun(true), 1000);
          } else {
            // Sync firestore state to local storage
            localStorage.setItem('americar_tour_status', 'completed');
          }
        }
      } catch (error) {
        console.error("Error checking tour state:", error);
      }
    };
    
    checkTourState();
  }, [vendorId]);

  const handleJoyrideCallback = async (data) => {
    console.log("Joyride Callback Data:", data); // Debugging log
    
    const { status, type } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    // Check both status array AND tour:end event just to be absolutely certain
    if (finishedStatuses.includes(status) || type === 'tour:end') {
      console.log("Tour marked as finished/skipped! Updating Firestore...");
      
      setRun(false);
      localStorage.setItem('americar_tour_status', 'completed'); // Immediate local lock
      
      if (vendorId) {
        try {
          const vendorRef = doc(db, 'vendorUsers', vendorId);
          await setDoc(vendorRef, { tourStatus: 'completed' }, { merge: true });
          console.log("Firestore successfully updated with tourStatus: 'completed'");
        } catch (error) {
          console.error("Error saving tour completion state to Firestore:", error);
        }
      } else {
        console.warn("Could not update Firestore: vendorId is missing!");
      }
    }
  };

  const steps = [
    {
      target: 'body',
      placement: 'center',
      title: 'Welcome to Americar! 👋',
      icon: FaCar,
      content: 'This is your brand new Vendor Dashboard. Let us take you on a quick 30-second tour to show you what you can do on every page.',
      disableBeacon: true,
    },
    {
      target: '#tour-dashboard-stats',
      title: 'Key Metrics',
      icon: FaChartLine,
      content: 'Here on your dashboard, you can see your key performance metrics at a glance, including your daily revenue and the total services your branches have performed today.',
      placement: 'bottom',
    },
    {
      target: '#tour-dashboard-charts',
      title: 'Service Analytics',
      icon: FaCheckCircle,
      content: 'These charts break down exactly what services your customers are buying, helping you see what is driving your business.',
      placement: 'top',
    },
    {
      target: '#tour-dashboard-transactions',
      title: 'Recent Transactions',
      icon: FaClipboardList,
      content: 'Keep track of payments and service statuses instantly with this real-time list of your most recent transactions.',
      placement: 'top',
    },
    {
      target: '#tour-link-branches',
      title: 'Manage Branches',
      icon: FaMapMarkerAlt,
      content: 'Use this sidebar link to add new branch locations, upload required verification documents, and assign branch managers.',
      placement: 'right',
    },
    {
      target: '#tour-link-reports',
      title: 'Detailed Reports',
      icon: FaRegFileAlt,
      content: 'Visit the reports section to view detailed analytics on your revenue and track the exact services performed across all your branches.',
      placement: 'right',
    },
    {
      target: '#tour-link-notifications',
      title: 'Notifications Inbox',
      icon: FaBell,
      content: 'All your alerts, branch approvals, and important system updates will show up right here in your notifications inbox.',
      placement: 'right',
    },
    {
      target: '#tour-link-settings',
      title: 'Vendor Settings',
      icon: FaCog,
      content: 'Crucial step: Before you can add any branches, you must complete your profile and link a Stripe account here so you can get paid!',
      placement: 'right',
    }
  ];

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      run={run}
      scrollToFirstStep
      showProgress={false}
      showSkipButton
      steps={steps}
      tooltipComponent={CustomTooltip}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
        },
      }}
    />
  );
};

export default OnboardingTour;
